// ingest.name-fix task (§ serial repair). After the serial pass, some names appear
// in the body but resolve to NO registry entity — the model missed a merge. Rather
// than mint ghost assets, we ask once: for each such name, is it another叫法 of an
// already-registered entity (→ merge), or a genuinely new entity (→ newEntity)?
// Output is a Delta with only newEntities + merges. New entities carry their
// required default look inline. Tool mode.

import type { Delta } from "../../../domain/ingest/serial-context.js";
import { NAME_FIX_SCHEMA } from "../schemas.js";
import type { LlmTaskExecutor } from "../../types.js";
import { SerialDeltaShapeError, toDelta } from "./episode-assets.js";

export const NAME_FIX_SYSTEM = [
  "你是剧本实体归一修复器。【已知设定】是已建立的实体登记（register）。下面【待归一名字】是正文里出现、但还没能对应到 register 里任何实体的名字。",
  "对**每一个**待归一名字，判断它是不是 register 里某个已登记实体的另一种叫法：",
  "  · **是**（字面变体如 大骨=大骨头、苏若雪=若雪；或剧情/身份能确定是同一个，如前世名/化名/全名短名）→ 放进 `merges`：{from: 这个名字, into: 已登记实体的 canonical}。",
  "  · **不是**（它确实是 register 里从没登记过的一个新实体）→ 放进 `newEntities`：{kind, name(纯 canonical), description(一句话身份), aliases, defaultLook:{name:'常态', description:可见外观}}。",
  "新人物的 canonical name 直接使用【待归一名字】里的原文姓名拼写；外语姓名不翻译、不音译、不改成中文名。",
  "人物 description 只写身份、角色和关系；defaultLook.description 写外貌、服饰等可直接复现的默认造型，两者不能互相代替。",
  "**不同角色绝不互相当别名**。拿不准是哪一个已有实体时，宁可当新实体登记，也不要 merge 错人。",
  "地点同理：同一个地方的不同写法（带不带平台/区域前缀、『车轮旁』=『矿车车轮旁』）merge 到已有那条；确实是新地方才 newEntity。",
  "只输出 merges 和 newEntities 两项，覆盖**每一个**待归一名字。",
].join("\n");

export function buildNameFixUser(contextMd: string, names: string[]): string {
  return [
    "【已知设定】（已登记的实体）",
    contextMd.trim() || "（空）",
    "",
    "【待归一名字】（正文里出现但未对应到任何已登记实体，逐个处理）",
    names.map((n) => `- ${n}`).join("\n"),
  ].join("\n");
}

// Runs the fix and returns a Delta carrying only its newEntities + merges.
export async function runNameFix(executor: LlmTaskExecutor, contextMd: string, names: string[], signal?: AbortSignal): Promise<{ delta: Delta; raw: string }> {
  const response = await executor.generateTool("ingest.name-fix", {
    system: NAME_FIX_SYSTEM,
    user: buildNameFixUser(contextMd, names),
    tool: { name: "emit_fix", description: "把待归一名字 merge 到已有或登记为新实体", inputSchema: NAME_FIX_SCHEMA },
    ...(signal ? { signal } : {}),
  });
  return { delta: nameFixToDelta(response.input, names), raw: JSON.stringify(response.input) };
}

const nameKey = (value: string): string => value.normalize("NFC").replace(/\s+/g, " ").trim();

export function nameFixToDelta(input: unknown, names: string[] = []): Delta {
  const delta = toDelta(input);
  const covered = new Set<string>();
  for (const merge of delta.merges) covered.add(nameKey(merge.from));
  for (const entity of delta.newEntities) {
    covered.add(nameKey(entity.name));
    for (const alias of entity.aliases) covered.add(nameKey(alias));
  }
  const missing = names.filter((name) => !covered.has(nameKey(name)));
  if (missing.length) {
    throw new SerialDeltaShapeError(`name-fix did not cover: ${missing.join("、")}`);
  }
  return delta;
}
